import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Signal, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useProjectCoverageSessions,
  useUnlinkCoverageSession,
  useMultipleSessionsMapData,
  useCoverageApiHealth,
} from "@/hooks/useNetworkCoverage";
import { CoverageMapViewer } from "./network-coverage/CoverageMapViewer";
import { CoverageSessionCard } from "./network-coverage/CoverageSessionCard";
import { LinkSessionDialog } from "./network-coverage/LinkSessionDialog";
import { CameraPlansSection } from "./camera-plans/CameraPlansSection";
import type { ProjectWithRelations } from "@/hooks/useProjects";

interface ProjectPlanTabProps {
  project: ProjectWithRelations;
  currentSubtab?: string;
  selectedCameraPlanId?: string;
  basePath: string;
}

export function ProjectPlanTab({
  project,
  currentSubtab,
  selectedCameraPlanId,
  basePath,
}: ProjectPlanTabProps) {
  const navigate = useNavigate();
  const subtab =
    currentSubtab === "network-coverage" ? "network-coverage" : "camera-plans";

  return (
    <Tabs
      value={subtab}
      onValueChange={(value) => navigate(`${basePath}/${value}`)}
      className="w-full"
    >
      <TabsList>
        <TabsTrigger value="camera-plans">Camera Plans</TabsTrigger>
        <TabsTrigger value="network-coverage">Network Coverage</TabsTrigger>
      </TabsList>

      <TabsContent value="camera-plans" className="mt-4">
        <CameraPlansSection
          projectId={project.id}
          previousEditionProjectId={project.previous_edition_project_id ?? null}
          projectDates={project}
          selectedPlanId={selectedCameraPlanId}
          basePath={`${basePath}/camera-plans`}
        />
      </TabsContent>

      <TabsContent value="network-coverage" className="mt-4">
        <NetworkCoverageSection project={project} />
      </TabsContent>
    </Tabs>
  );
}

// Extracted from the original ProjectPlanTab — all network coverage logic stays intact
function NetworkCoverageSection({
  project,
}: {
  project: ProjectWithRelations;
}) {
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [visibleSessionIds, setVisibleSessionIds] = useState<Set<string>>(
    new Set(),
  );

  const { data: sessions = [], isLoading } = useProjectCoverageSessions(
    project.id,
  );
  const { data: healthData } = useCoverageApiHealth();
  const unlinkSession = useUnlinkCoverageSession();

  const visibleExternalIds = useMemo(() => {
    return sessions
      .filter((s) => visibleSessionIds.has(s.id))
      .map((s) => s.external_session_id);
  }, [sessions, visibleSessionIds]);

  const { data: mapDataArray = [], isLoading: isLoadingMap } =
    useMultipleSessionsMapData(visibleExternalIds);

  useEffect(() => {
    if (sessions.length > 0 && visibleSessionIds.size === 0) {
      setVisibleSessionIds(new Set(sessions.map((s) => s.id)));
    }
  }, [sessions, visibleSessionIds.size]);

  const toggleVisibility = (sessionId: string) => {
    setVisibleSessionIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  };

  const showAll = () => {
    setVisibleSessionIds(new Set(sessions.map((s) => s.id)));
  };

  const hideAll = () => {
    setVisibleSessionIds(new Set());
  };

  const handleUnlink = (sessionId: string) => {
    unlinkSession.mutate({ id: sessionId, projectId: project.id });
    setVisibleSessionIds((prev) => {
      const next = new Set(prev);
      next.delete(sessionId);
      return next;
    });
  };

  const sessionsForMap = useMemo(() => {
    return sessions.map((session) => {
      const mapData = mapDataArray.find(
        (d) => d.id === session.external_session_id,
      );
      return {
        id: session.id,
        points: mapData?.points || [],
        visible: visibleSessionIds.has(session.id),
        deviceName: session.device_name || undefined,
      };
    });
  }, [sessions, mapDataArray, visibleSessionIds]);

  const linkedExternalIds = sessions.map((s) => s.external_session_id);
  const apiConnected = healthData?.status === "ok";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="space-y-6">
        <div className="h-[400px] bg-muted rounded-lg flex items-center justify-center">
          <div className="text-center">
            <Signal className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              No coverage sessions linked to this project
            </p>
            <Button offlineEdit onClick={() => setIsLinkDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Link Session
            </Button>
          </div>
        </div>

        {!apiConnected && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                Coverage API is not connected. Make sure the
                network-coverage-tester service is running.
              </p>
            </CardContent>
          </Card>
        )}

        <LinkSessionDialog
          projectId={project.id}
          linkedSessionIds={linkedExternalIds}
          open={isLinkDialogOpen}
          onOpenChange={setIsLinkDialogOpen}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <CoverageMapViewer
        sessions={sessionsForMap}
        isLoading={isLoadingMap}
        className="h-[500px]"
      />

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Linked Sessions</h3>
          <p className="text-sm text-muted-foreground">
            {sessions.length} session{sessions.length !== 1 ? "s" : ""} linked
            to this project
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={showAll}>
            <Eye className="h-4 w-4 mr-2" />
            Show All
          </Button>
          <Button variant="outline" size="sm" onClick={hideAll}>
            <EyeOff className="h-4 w-4 mr-2" />
            Hide All
          </Button>
          <Button offlineEdit onClick={() => setIsLinkDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Link Session
          </Button>
        </div>
      </div>

      <div className="grid gap-4">
        {sessions.map((session) => (
          <CoverageSessionCard
            key={session.id}
            session={session}
            visible={visibleSessionIds.has(session.id)}
            onToggleVisibility={() => toggleVisibility(session.id)}
            onUnlink={() => handleUnlink(session.id)}
          />
        ))}
      </div>

      <LinkSessionDialog
        projectId={project.id}
        linkedSessionIds={linkedExternalIds}
        open={isLinkDialogOpen}
        onOpenChange={setIsLinkDialogOpen}
      />
    </div>
  );
}
